import { baseRequest } from '@bitnimbus/api-v4/lib/request';
import { APIError, StatusCode } from '@bitnimbus/api-v4';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as React from 'react';
import { SupportError } from './components/SupportError';
import { MigrateError } from 'src/components/MigrateError';
import { VerificationError } from 'src/components/VerificationError';
import { ACCESS_TOKEN, API_ROOT, DEFAULT_ERROR_MESSAGE } from 'src/constants';
import { handleLogout } from 'src/store/authentication/authentication.actions';
import { setErrors } from 'src/store/globalErrors/globalErrors.actions';
import { interceptErrors } from 'src/utilities/interceptAPIError';
import { getEnvLocalStorageOverrides } from './utilities/storage';
import { ApplicationStore } from './store';

const handleSuccess: <T extends AxiosResponse<any>>(response: T) => T | T = (
  response
) => {
  if (!!response.headers['x-maintenance-mode']) {
    Promise.reject(response);
  }

  return response;
};

export const handleError = (error: AxiosError, store: ApplicationStore) => {
  if (error.response && error.response.status === 401) {
    /**
     * this will blow out redux state and the componentDidUpdate in the
     * AuthenticationWrapper.tsx will be responsible for redirecting to Login
     */
    store.dispatch(handleLogout());
  }
  const config = error.response?.config ?? {};
  const url = config.url ?? '';
  const status: number = error.response?.status ?? 0;
  let errors: APIError[] = error.response?.data?.errors ?? [
    { reason: DEFAULT_ERROR_MESSAGE },
  ];

  let errMsg = error.response?.data?.detail?.msg;
  if (!errMsg) {
    errMsg = error.response?.data?.message;
  }

  if (errMsg) {
    errors = [{ reason: errMsg, code: error.response?.data?.code }];
  }

  const apiInMaintenanceMode = !!error.response?.headers['x-maintenance-mode'];

  if (apiInMaintenanceMode) {
    store.dispatch(
      setErrors({
        api_maintenance_mode: true,
      })
    );
  }

  /** AxiosError contains the original POST data as stringified JSON */
  let requestData;
  try {
    requestData = JSON.parse(error.config?.data ?? '');
  } catch {
    requestData = {};
  }
  const requestedLinodeType = requestData?.type ?? '';

  const interceptedErrors = interceptErrors(errors, [
    {
      replacementText: (
        <VerificationError
          title={
            requestedLinodeType.match(/gpu/i)
              ? 'GPU Request'
              : 'Verification Request'
          }
        />
      ),
      condition: (e) => !!e.reason.match(/verification is required/i),
    },
    {
      replacementText: 'Please verify your Mobile Number to use our services',
      condition: (e) => e.code === StatusCode.MOBILE_NOT_VERIFIED,
      callback: () => {
        if (store && !store.getState().globalErrors.MOBILE_NOT_VERIFIED) {
          store.dispatch(
            setErrors({
              MOBILE_NOT_VERIFIED: true,
            })
          );
        }
      },
    },
    {
      replacementText: <MigrateError />,
      condition: (e) => {
        return (
          !!e.reason.match(/migrations are currently disabled/i) &&
          !!url.match(/migrate/i)
        );
      },
    },
    {
      replacementText: <SupportError errors={errors} />,
      condition: (e) => {
        return !!e.reason.match(/.*open a support ticket/i) && !e.field;
      },
    },
  ]);
  // Downstream components should only have to handle ApiFieldErrors, not AxiosErrors.
  return Promise.reject(interceptedErrors);
};

export const getURL = ({ url, baseURL }: AxiosRequestConfig) => {
  if (!url || !baseURL) {
    return;
  }

  const localStorageOverrides = getEnvLocalStorageOverrides();

  const apiRoot = localStorageOverrides?.apiRoot ?? API_ROOT;

  // If we have environment overrides in local storage, use those. Otherwise,
  // override the baseURL (from @bitnimbus/api-v4) with the one we have defined
  // in the environment (via .env file).
  return url.replace(baseURL, apiRoot);
};

// A user's external UUID can be found on the response to /account.
// Since that endpoint is not available to restricted users, the API also
// returns it as an HTTP header ("X-Customer-Uuid"). This middleware injects
// the value of the header to the GET /profile response so it can be added to
// the Redux store and used throughout the app.
export const injectEuuidToProfile = (
  response: AxiosResponse
): AxiosResponse => {
  if (isSuccessfulGETProfileResponse(response)) {
    const xCustomerUuidHeader = getXCustomerUuidHeader(response);
    if (xCustomerUuidHeader) {
      const profileWithEuuid = {
        ...response.data,
        _euuidFromHttpHeader: xCustomerUuidHeader,
      };

      return {
        ...response,
        data: profileWithEuuid,
      };
    }
  }
  return response;
};

export const isSuccessfulGETProfileResponse = (response: AxiosResponse) => {
  const { config, status } = response;

  const method = config.method?.toLowerCase();
  const url = config.url?.toLowerCase();

  return method === 'get' && status === 200 && url?.endsWith('/profile');
};

export const getXCustomerUuidHeader = (
  response: AxiosResponse
): string | undefined => {
  return response.headers['x-customer-uuid'];
};

export const setupInterceptors = (store: ApplicationStore) => {
  baseRequest.interceptors.request.use((config) => {
    const state = store.getState();
    /** Will end up being "Admin: 1234" or "Bearer 1234" */
    const token = ACCESS_TOKEN || (state.authentication?.token ?? '');

    const url = getURL(config);

    return {
      ...config,
      url,
      headers: {
        ...config.headers,
        ...(token && { Authorization: `${token}` }),
      },
    };
  });

  /*
  Interceptor that:
    * initiates re-authentication if the response is HTTP 401 "Unauthorized"
    * displays a Maintenance view if the API is in Maintenance mode
  Also rejects non-error responses if the API is in Maintenance mode
  */
  baseRequest.interceptors.response.use(handleSuccess, (error: AxiosError) =>
    handleError(error, store)
  );
};
